/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Typography} from "@mui/material";
import {useEffect, useMemo, useRef, useState} from "react";
import {MERGED_DATASET_TREE_TYPE, REDLINE_DIALOG_OPTIONS, SELECTED_DATASET_ID_URL_PARAM} from "../../../pages/CoverageRequirements/ItemDetails/constants";
import ArgosFlexbox from "../../Containers/ArgosFlexbox";
import useFetchUserData from "../../../../hooks/useFetchUserData";
import {usePostRequest} from "../../../../api/helper";
import useGetUrlParams from "../../../../hooks/useGetUrlParams";
import ArgosButton, {ArgosLoadingButton} from "../../Buttons/ArgosButton";
import SeparatorLine from "../../Separator/SeparatorLine";
import {findNodePath} from "../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/helper/helper";

const DeleteFolderDialogContent = (props: any) => {
  const {userData, isLoading} = useFetchUserData()
  const handleFolderDelete = () => {
    props.action(REDLINE_DIALOG_OPTIONS.DELETE, null)
    props.onClose()
  }

  const {studyId} = useGetUrlParams()

  const postHandleAreDatasetsUsedInMerge = usePostRequest('/HttpTriggerCoverageRequirements?request_operation=areDatasetsUsedInMerge')
  const [areDatasetsUsedInMerge, setAreDatasetsUsedInMerge] = useState<null | boolean>(null)
  const [usedMergeRes, setUsedMergeRes] = useState<any>(null)

  const datasetsUsedJsx = useMemo(() => {
    if (!usedMergeRes || !areDatasetsUsedInMerge) return []

    const {sourceDatasetIdToName, mergeSetIdToName, datasetIdToMergedDatasetNames} = usedMergeRes

    // Make a structure that allows showing the dataset name as the first level list, then the merge sets and merged datasets as nested bullet points
    let msgJsx = []
    for (let datasetId in datasetIdToMergedDatasetNames) {
      let mergeSets = datasetIdToMergedDatasetNames[datasetId]
      let datasetName = sourceDatasetIdToName[datasetId]
      let mergeSetsJsx = Object.keys(mergeSets).map((mergeSetId: string) => {
        let mergeSetName = mergeSetIdToName[mergeSetId]
        let mergedDatasetsJsx = mergeSets[mergeSetId].map((mergedDatasetName: string) => {
          return <li key={mergedDatasetName}>{mergedDatasetName}</li>
        })
        return (
          <li key={mergeSetId}>
            {mergeSetName}
            <ul>{mergedDatasetsJsx}</ul>
          </li>
        )
      })
      msgJsx.push(
        <li key={datasetId}>
          {datasetName}
          <ul>{mergeSetsJsx}</ul>
        </li>
      )
    }
    return msgJsx
  }, [usedMergeRes, areDatasetsUsedInMerge])
  const [isLoadingCheckUsedInMerge, setIsLoadingCheckUsedInMerge] = useState(false)

  const isMergedTree = props.treeType === MERGED_DATASET_TREE_TYPE

  useEffect(() => {
    if (!props.data?.nodes || isMergedTree) return
    const data = props.data?.nodes
    let datasetIds = []
    for (let i = 0; i < data.length; i++) {
      let node = data[i]
      if (node.nodeType !== 'dataset') continue
      datasetIds.push(node.datasetId)
    }
    if (datasetIds.length === 0) return
    setIsLoadingCheckUsedInMerge(true)
    postHandleAreDatasetsUsedInMerge({datasetIds: datasetIds, studyId}).then((res) => {
      if (!res)
        setAreDatasetsUsedInMerge(false)
      else {
        setAreDatasetsUsedInMerge(true)
        setUsedMergeRes(res)
      }
    }).catch((err) => {
      console.log(err)
    }).finally(() => {
      setIsLoadingCheckUsedInMerge(false)
    })

  }, [props.data?.nodes, studyId]);

  // check if all nodes selected are folders
  const allFolders = props.data?.nodes?.every((node: any) => node.nodeType === 'folder' || node.nodeType === 'study') ?? false

  // check if the only node selected is study
  const isOnlyStudy = props.data?.nodes?.length === 1 && props.data?.nodes[0].nodeType === 'study'

  // const downstreamEffectsMessage = useRef(`This could have downstream effects on merged ${VAR_COMMON_CONSTANTS.VAR_DATA_SETS}.`)
  const downstreamEffectsMessage = useRef(``)
  const deleteDataMessage = useMemo(() =>
    (`Are you sure you want to delete checked data? Note: Deleting folder(s) will also delete all content within. Do you want to continue? ${!isMergedTree ? downstreamEffectsMessage.current : ""}`), [props.treeType])
  const deleteDatasetMessage = useMemo(() =>
    (`Are you sure you want to delete this dataset? ${!isMergedTree ? downstreamEffectsMessage.current : ""}`), [props.treeType])
  const deleteFolderMessage = useMemo(() =>
    (`Deleting this folder will also delete all its contents. Do you want to continue? ${!isMergedTree ? downstreamEffectsMessage.current : ""}`), [props.treeType])
  let warningHeader = isMergedTree ? '' : !props.data?.contextNodeId ? deleteDataMessage : props.isdataset ? deleteDatasetMessage : deleteFolderMessage

  const msg = useMemo(() => {
    if (!isMergedTree) {
      // data is list of nodes. get the user of each node and the node name and display those in bullet points. If no owner, then skip that bullet point
      if (!props.data?.nodes) return
      const data = props.data?.nodes
      let msgJsx = []
      for (let i = 0; i < data.length; i++) {
        let node = data[i]
        if (node.nodeType !== 'dataset') continue

        const breadcrumbs = findNodePath(props?.treeData, SELECTED_DATASET_ID_URL_PARAM, node.datasetId ?? '','name').join("/")+"/"
        let owner = node.owner
        let nodeName = breadcrumbs+node.name
        const fullName = userData?.[owner]?.fullName ?? `gmid: ${owner}`
        if (owner) {
          msgJsx.push(<li key={i}>{nodeName} - {fullName}</li>)
        } else {
          msgJsx.push(<li key={i}>{nodeName}</li>)
        }
      }
      return msgJsx
    }
  }, [props.data, props.treeType, userData])

  let msgjsx = (
      <>
        <Typography>
          {warningHeader}
        </Typography>
        {!allFolders && !isOnlyStudy && !isMergedTree &&
        <div style={{marginTop: 15, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column'}}>
          <div>Selected datasets to delete and their owners:</div>
          <ul style={{marginTop: 2, flex: 1, overflow: 'auto'}}>
            {msg}
          </ul>
        </div>
        }
      </>
    )

  const containerStyle = isMergedTree ? {overflow: 'auto', maxWidth: '30vw'} : {maxWidth: '70vw', overflow: 'auto', height: '70vh'}

  return (
    <ArgosFlexbox vertical style={containerStyle}>
      <div style={{flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column'}}>
        {msgjsx}
      </div>

      {(isLoadingCheckUsedInMerge || areDatasetsUsedInMerge) &&
        <SeparatorLine/>
      }

      {isLoadingCheckUsedInMerge &&
        <div>
          <Typography>Checking if datasets used in merges...</Typography>
        </div>
      }
      {areDatasetsUsedInMerge &&
        <div>
          <Typography sx={{color: 'red', fontSize: 15}}>Cannot delete dataset(s) as they are used in merge set(s).</Typography>
        </div>
      }

      {areDatasetsUsedInMerge && datasetsUsedJsx &&
        <div style={{flex: 1, overflow: 'auto'}}>
          <ul>{datasetsUsedJsx}</ul>
        </div>
      }
      <ArgosFlexbox style={{justifyContent: "center", marginBottom: 1}}>
        <ArgosButton onClick={() => props.onClose()}>
          CANCEL
        </ArgosButton>
        <ArgosLoadingButton disabled={!(allFolders && !isOnlyStudy) && props.treeType !== MERGED_DATASET_TREE_TYPE && (areDatasetsUsedInMerge == null || areDatasetsUsedInMerge || isOnlyStudy)} onClick={() => handleFolderDelete()}>
          DELETE
        </ArgosLoadingButton>
      </ArgosFlexbox>
    </ArgosFlexbox>
  );
}
export default DeleteFolderDialogContent;
